import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Sparkles } from 'lucide-react';
import { useCreateCompetenceRecord, useGetUsers, customInstance } from '@edison/api-client-react';
import { useAuthStore } from '@/store/auth-store.js';
import { Button } from '@/components/ui/button.js';

interface UserProfile {
  qualifications: string | null;
  relevantExpertise: string | null;
  priorWorkSummary: string | null;
}

function useMyProfile() {
  return useQuery<{ data: UserProfile | null }>({
    queryKey: ['user-profile'],
    queryFn: () => customInstance({ url: '/api/users/me/profile', method: 'GET' }),
  });
}

interface Props {
  activityId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormValues {
  qualifications: string;
  relevantExpertise: string;
  priorWorkSummary: string;
  roleOnActivity: string;
  uncertaintyBasis: string;
  targetUserId: string;
}

const MANAGE_ROLES = new Set(['tenant_admin', 'r_and_d_manager']);

export function CompetenceRecordForm({ activityId, onSuccess, onCancel }: Props) {
  const { user } = useAuthStore();
  const isManager = user?.role ? MANAGE_ROLES.has(user.role) : false;

  const { mutate, isPending, error: mutationError } = useCreateCompetenceRecord();
  const { data: usersData } = useGetUsers();
  const { data: profileData, isLoading: profileLoading } = useMyProfile();

  const profile = profileData?.data;
  const hasProfile = !!(profile?.qualifications || profile?.relevantExpertise || profile?.priorWorkSummary);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { targetUserId: '' } });

  const targetUserId = watch('targetUserId');
  const otherUsers = (usersData?.data?.items ?? []).filter((u) => u.id !== user?.id);

  // Pre-fill AI-extractable fields from the user's own profile once loaded
  useEffect(() => {
    if (!profile || targetUserId) return; // don't pre-fill when adding on behalf of someone else
    reset((prev) => ({
      ...prev,
      qualifications:    profile.qualifications    ?? prev.qualifications,
      relevantExpertise: profile.relevantExpertise ?? prev.relevantExpertise,
      priorWorkSummary:  profile.priorWorkSummary  ?? prev.priorWorkSummary,
    }));
  }, [profile, targetUserId, reset]);

  const onSubmit = (values: FormValues) => {
    mutate(
      {
        activityId,
        roleOnActivity:    values.roleOnActivity,
        uncertaintyBasis:  values.uncertaintyBasis,
        ...(values.qualifications    ? { qualifications:    values.qualifications }    : {}),
        ...(values.relevantExpertise ? { relevantExpertise: values.relevantExpertise } : {}),
        ...(values.priorWorkSummary  ? { priorWorkSummary:  values.priorWorkSummary }  : {}),
        ...(values.targetUserId      ? { targetUserId:      values.targetUserId }      : {}),
      },
      { onSuccess }
    );
  };

  const assessorLabel = targetUserId
    ? (otherUsers.find((u) => u.id === targetUserId)?.name ?? 'selected user')
    : 'you';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-lg border p-4 bg-card">
      <h3 className="font-medium text-sm">Add competence record</h3>

      {isManager && otherUsers.length > 0 && (
        <div>
          <label htmlFor="targetUserId" className="block text-sm font-medium mb-1">
            Adding record for
          </label>
          <select
            id="targetUserId"
            {...register('targetUserId')}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
          >
            <option value="">Myself ({user?.name ?? user?.email})</option>
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ?? u.email}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            As an admin you can add a record on behalf of a team member. They will need to confirm it.
          </p>
        </div>
      )}

      {/* Profile-sourced fields */}
      {!targetUserId && hasProfile && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground rounded-md bg-muted/50 px-3 py-2">
          <Sparkles className="h-3 w-3 shrink-0" aria-hidden="true" />
          Pre-filled from your profile — edit freely before saving.
        </div>
      )}
      {!targetUserId && !profileLoading && !hasProfile && (
        <p className="text-xs text-muted-foreground">
          The fields below can be populated automatically if you{' '}
          <a href="/profile" className="underline hover:text-foreground">add your profile</a>.
        </p>
      )}

      <div>
        <label htmlFor="qualifications" className="block text-sm font-medium mb-1">
          Qualifications
        </label>
        <textarea
          id="qualifications"
          rows={2}
          {...register('qualifications')}
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
          placeholder="Relevant qualifications and credentials..."
        />
      </div>

      <div>
        <label htmlFor="relevantExpertise" className="block text-sm font-medium mb-1">
          Relevant expertise
        </label>
        <textarea
          id="relevantExpertise"
          rows={2}
          {...register('relevantExpertise')}
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
          placeholder="Domain expertise relevant to this activity..."
        />
      </div>

      <div>
        <label htmlFor="priorWorkSummary" className="block text-sm font-medium mb-1">
          Prior work summary
        </label>
        <textarea
          id="priorWorkSummary"
          rows={2}
          {...register('priorWorkSummary')}
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
          placeholder="Summary of prior work in this area..."
        />
      </div>

      <div className="pt-2 border-t">
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 mb-3">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-1" />
          <span>
            The following two fields <strong>must be written by {assessorLabel}</strong>. Do not use
            AI-generated or AI-suggested text. AusIndustry assessors may interview{' '}
            {targetUserId ? 'them' : 'you'} directly on these assessments.
          </span>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="roleOnActivity" className="block text-sm font-medium mb-1">
              {targetUserId ? `${assessorLabel}'s` : 'Your'} role on this activity{' '}
              <span className="text-destructive">*</span>
            </label>
            <textarea
              id="roleOnActivity"
              rows={3}
              {...register('roleOnActivity', { required: 'Role on activity is required' })}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
              placeholder="Describe the specific role on this activity in their own words..."
            />
            {errors.roleOnActivity && (
              <p className="mt-1 text-xs text-destructive">{errors.roleOnActivity.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="uncertaintyBasis" className="block text-sm font-medium mb-1">
              Basis for technical uncertainty <span className="text-destructive">*</span>
            </label>
            <textarea
              id="uncertaintyBasis"
              rows={3}
              {...register('uncertaintyBasis', { required: 'Uncertainty basis is required' })}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
              placeholder="Explain why, in their expert opinion, this activity involves technical uncertainty..."
            />
            {errors.uncertaintyBasis && (
              <p className="mt-1 text-xs text-destructive">{errors.uncertaintyBasis.message}</p>
            )}
          </div>
        </div>
      </div>

      {mutationError && (
        <p className="text-sm text-destructive">Failed to save record. Please try again.</p>
      )}

      <div className="flex gap-3">
        <Button type="submit" variant="primary" disabled={isPending || profileLoading}>
          {isPending ? 'Saving...' : 'Save record'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

interface ConfirmRecordProps {
  activityId: string;
  recordId: string;
  onConfirmed: () => void;
}

export function ConfirmRecordButton({ activityId, recordId, onConfirmed }: ConfirmRecordProps) {
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      customInstance({
        url: `/api/activities/${activityId}/competence-records/${recordId}/confirm`,
        method: 'POST',
      }),
    onSuccess: onConfirmed,
  });

  return (
    <Button
      variant="primary"
      size="sm"
      disabled={isPending}
      onClick={() => mutate()}
    >
      <CheckCircle className="h-3 w-3" aria-hidden="true" />
      {isPending ? 'Confirming…' : 'Confirm this record'}
    </Button>
  );
}
